import React, { useState, useEffect } from 'react';
import { responseHero } from '../../../utils/axiosInterceptor';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../store/reducers/userSlice';
import { User } from '../../../models/user.model';
import { getUsers } from '../../../api/getUsers';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const AddTaskModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
    const user = useSelector(selectUser);
    
    // Csak az admin usereket tároljuk ebben a listában
    const [adminUsers, setAdminUsers] = useState<User[]>([]);
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        assigneeId: '', // Felelős ID-ja
    });

    // Felhasználók betöltése és szűrése
    useEffect(() => {
        if (!isOpen) return;

        const fetchUsers = async () => {
            try {
                // Lekérjük az összes felhasználót
                const res = await getUsers();
                
                // Csak azokat tartjuk meg, akik adminok
                const filteredAdmins = (res as User[]).filter(u => u.admin === true);
                setAdminUsers(filteredAdmins);
            } catch (err) {
                console.error("Nem sikerült betölteni a felhasználókat:", err);
            }
        };

        fetchUsers();
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Kiolvassuk a bejelentkezett user ID-ját automatikusan
        const currentUserId = user?.userInfo?.user_id;

        if (!currentUserId) {
            alert("Hiba: Nincs bejelentkezett felhasználó (Létrehozó)!");
            return;
        }

        if (!formData.title || !formData.assigneeId) {
            alert("Minden kötelező mezőt tölts ki!");
            return;
        }

        try {
            // Összerakjuk a pontos payload-ot a backendnek
            const payload = {
                title: formData.title,
                description: formData.description,
                creatorId: Number(currentUserId), // Automatikus
                assigneeId: Number(formData.assigneeId), // Kiválasztott admin
                taskState: 'TODO' // Fixen TODO
            };

            await responseHero.post(`${process.env.REACT_APP_API_URL}/tasks`, { data: payload });
            
            // Sikeres mentés után űrlap alaphelyzetbe állítása
            setFormData({
                title: '',
                description: '',
                assigneeId: '',
            });
            
            onSuccess();
        } catch (err: any) {
            console.error("Backend hiba:", err.response?.data);
            alert(`Hiba a mentés során: ${err.response?.data?.message || "Ismeretlen hiba"}`);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                
                {/* Header */}
                <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-blue-50/30">
                    <div>
                        <h3 className="text-xl font-black text-slate-800 tracking-tight">Új Feladat</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase">Teendő kiírása</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-3xl transition-colors">&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-5">
                    {/* Feladat Címe */}
                    <div>
                        <label className="block text-[10px] font-black uppercase text-blue-600 mb-1">Feladat címe</label>
                        <input 
                            type="text" 
                            required 
                            placeholder="Pl. Szerver logok ellenőrzése"
                            className="w-full bg-blue-50/30 border border-blue-100 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-2 ring-blue-500" 
                            value={formData.title} 
                            onChange={(e) => setFormData({...formData, title: e.target.value})} 
                        />
                    </div>

                    {/* Leírás */}
                    <div>
                        <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Részletes leírás</label>
                        <textarea 
                            required
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ring-blue-500"
                            rows={4}
                            placeholder="A feladat pontos leírása..."
                            value={formData.description}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                        />
                    </div>

                    {/* Felelős Admin (Assignee) Választó */}
                    <div>
                        <label className="block text-[10px] font-black uppercase text-slate-400 mb-1">Feladat kiírása neki:</label>
                        <select 
                            required
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-700 outline-none focus:ring-2 ring-blue-500" 
                            value={formData.assigneeId} 
                            onChange={(e) => setFormData({...formData, assigneeId: e.target.value})}
                        >
                            <option value="">-- Válassz valakit --</option>
                            {adminUsers.map(admin => (
                                <option key={admin.user_id} value={admin.user_id}>
                                    {admin.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Információs mező, csak hogy látszódjon a logikád */}
                    <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase px-1">
                        <span>Létrehozó: <strong className="text-slate-600">{user?.userInfo?.name || 'Te'}</strong></span>
                        <span>Kezdő állapot: <strong className="text-blue-600">TODO</strong></span>
                    </div>

                    {/* Submit Gomb */}
                    <button 
                        type="submit"
                        disabled={!formData.title || !formData.assigneeId}
                        className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-white transition-all shadow-lg active:scale-95 ${
                            formData.title && formData.assigneeId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'
                        }`}
                    >
                        Feladat Kiírása
                    </button>
                </form>
            </div>
        </div>
    );
};